import React, {useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import SuppliesService from "../../../../services/web/supplies-service.js";
import {
    formatStoredFuelQuantity,
    formatStoredMoneyValue,
    MaskDefault,
    parseStoredFuelQuantity,
    parseStoredMoneyValue
} from "../../../../utils/mask.js";
import projectUtils from "../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {exportObjectsToXlsx} from "../../../../utils/excel-export-utils.js";

const initialFilters = {
    prefix: "",
    plate: "",
    name: "",
    gas_station: "",
    dtStart: null,
    dtEnd: null,
    fuel_type: ""
};

const fuelTypeOptions = [
    { label: "Arla 32 (litros)", value: "arla32" },
    { label: "Diesel (litros)", value: "diesel" },
    { label: "Gasolina (litros)", value: "gasoline" },
    { label: "Etanol (litros)", value: "ethanol" },
    { label: "GNV (m³)", value: "gnv" },
    { label: "Elétrico (kW)", value: "electric" }
];

const SuppliesList = () => {
    const navigate = navigateHook();
    const gridRef = useRef(null);

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);

    const { isLoading } = useQuery({
        queryKey: ["supplies"],
        queryFn: () =>
            SuppliesService.list().then(({ data }) => {
                setData(data);
                setFilteredData(data);
                return data;
            })
    });

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const columnDefs = [
        { headerName: "Prefixo", field: "prefix", width: 110 },
        { headerName: "Placa", field: "plate", width: 110, comparator: aggridUtils.nullLastComparator,
            cellRenderer: ({ value }) => <div className="font-bold">{projectUtils.formatPlate(value)}</div>
        },
        { headerName: "Condutor", field: "name", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Posto", field: "gas_station", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Data/Hora", field: "date", sort: "desc", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        { headerName: "Tipo combustível", field: "fuel_type", width: 170, comparator: aggridUtils.nullLastComparator },
        { headerName: "Valor", field: "value_pay", width: 120,
            valueFormatter: ({ value }) => formatStoredMoneyValue(value, {style: "currency", currency: "BRL"}),
            comparator: (a, b, nodeA, nodeB, isInverted) => aggridUtils.nullLastComparator(parseStoredMoneyValue(a), parseStoredMoneyValue(b), nodeA, nodeB, isInverted)
        },
        { headerName: "Quant.", field: "fuel_quant", width: 100,
            valueFormatter: ({ value }) => formatStoredFuelQuantity(value),
            comparator: (a, b, nodeA, nodeB, isInverted) => aggridUtils.nullLastComparator(parseStoredFuelQuantity(a), parseStoredFuelQuantity(b), nodeA, nodeB, isInverted)
        },
        { headerName: "Km ao abastecer", field: "current_km", flex: 1, comparator: aggridUtils.nullLastComparator }
    ];

    const onChange = ({ target: { id, value } }) => {
        setFilters(prev => ({ ...prev, [id]: value }));
    };

    const normalize = (value) => value ? value.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") : "";

    const parseDateTime = (value) => {
        if (!value) return null;

        const [datePart, timePart = "00:00"] = value.split(" ");
        const [day, month, year] = datePart.split("/");
        const [hour = 0, minute = 0] = timePart.split(":");

        return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute)
        );
    };

    const apply = () => {
        const parseDate = (value) => {
            if (!value) return null;

            const [datePart, timePart = "00:00"] = value.split(" ");
            const [day, month, year] = datePart.split("/");
            const [hour = 0, minute = 0] = timePart.split(":");

            return new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hour),
                Number(minute)
            );
        };

        const start = filters.dtStart ? new Date(new Date(filters.dtStart).setHours(0, 0, 0, 0)) : null;
        const end = filters.dtEnd ? new Date(new Date(filters.dtEnd).setHours(23, 59, 59, 999)) : null;

        const result = data.filter(item => {
            const date = parseDate(item.date);

            return (
                (!filters.prefix || normalize(item.prefix).includes(normalize(filters.prefix))) &&
                (!filters.plate || normalize(item.plate).includes(normalize(filters.plate.replace(/\W/g, "")))) &&
                (!filters.name || normalize(item.name).includes(normalize(filters.name))) &&
                (!filters.gas_station || normalize(item.gas_station).includes(normalize(filters.gas_station))) &&
                (!filters.fuel_type || normalize(item.fuel_type) === normalize(filters.fuel_type)) &&
                (!start || (date && date >= start)) &&
                (!end || (date && date <= end))
            );
        });

        setFilteredData(result);
    };

    const clearFilter = () => {
        setFilters(initialFilters);
        setFilteredData(data);
    };

    const newSupply = () => {
        const urlDev = "/supplies/form";
        navigate(urlDev, true);
    };

    const onCellClicked = ({ data, event }) => {
        const urlDev = `/supplies/form/${data.id}`;
        
        navigate(urlDev, event.ctrlKey);
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);

    const exportCSV = async () => {
        if (!filteredData.length) return;

        const abastecimentos = filteredData.map(s => ({
            "Prefixo": s.prefix,
            "Placa": projectUtils.formatPlate(s.plate),
            "Condutor": s.name,
            "Posto": s.gas_station,
            "Data/Hora": s.date,
            "Tipo combustível": s.fuel_type,
            "Valor": formatStoredMoneyValue(s.value_pay, {
                style: "currency",
                currency: "BRL"
            }),
            "Quantidade": formatStoredFuelQuantity(s.fuel_quant),
            "Km ao abastecer": s.current_km
        }));

        await exportObjectsToXlsx({
            fileName: "abastecimentos.xlsx",
            sheetName: "Abastecimentos",
            data: abastecimentos
        });
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Abastecimentos</Template.Title>
                <div className="flex gap-2">
                    <Button target="success" onClick={exportCSV}>
                        <FaFileExcel /> Exportar
                    </Button>
                    <Button onClick={newSupply}>
                        <FaPlus /> Adicionar abastecimento
                    </Button>
                </div>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                            <InputForm
                                id="prefix"
                                label="Prefixo"
                                value={filters.prefix}
                                onChange={onChange}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id="plate"
                                label="Placa"
                                value={filters.plate}
                                onChange={onChange}
                                mask={MaskDefault.plate}
                                inputClass={"uppercase"}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id="name"
                                label="Condutor"
                                value={filters.name}
                                onChange={onChange}
                                classNameWrapper="col-span-6"
                            />

                            <InputForm
                                id="gas_station"
                                label="Posto"
                                value={filters.gas_station}
                                onChange={onChange}
                                classNameWrapper="col-span-4"
                            />

                            <div className={"col-span-4 flex items-stretch"}>
                                <InputDatetime
                                    id="dtStart"
                                    label="Período entre"
                                    selected={filters.dtStart}
                                    onChange={date => setFilters(prev => ({...prev, dtStart: date}))}
                                    dateFormat="L"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[47%]"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    id="dtEnd"
                                    selected={filters.dtEnd}
                                    onChange={date => setFilters(prev => ({...prev, dtEnd: date}))}
                                    dateFormat="L"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="w-[47%]"
                                />
                            </div>

                            <div className="col-span-4 flex gap-4 items-end">
                                <SelectComponent
                                    label="Tipo combustível"
                                    value={filters.fuel_type ? fuelTypeOptions.find(o => o.label === filters.fuel_type) : null}
                                    onChange={option => setFilters(prev => ({...prev, fuel_type: option?.label ?? ""}))}
                                    options={fuelTypeOptions}
                                    classNameWrapper="flex-1"
                                />

                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" onClick={clearFilter}>
                                        <FaEraser />
                                    </Button>

                                    <Button onClick={apply}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className="h-[calc(100vh-196px)] w-full aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                suppressCellFocus
                                animateRows={false}
                                onCellClicked={onCellClicked}
                                gridOptions={{ localeText: aggridUtils.localization() }}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default SuppliesList;
